import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { RouterModule } from '@angular/router';
import { AnalyticsService } from '../../../../shared/services/analytics.service';

export interface GettingStartedPath {
  id: string;
  eyebrow: string;
  title: string;
  description: string;
  supportingNote?: string;
  offerRule?: string;
  terms: string[];
  guardrail?: string;
  badge?: string;
  highlighted?: boolean;
  ctaLabel: string;
  ctaLink: string;
  queryParams?: Record<string, string>;
  analyticsId: string;
}

@Component({
  selector: 'app-getting-started-section',
  standalone: true,
  imports: [CommonModule, MatButtonModule, RouterModule],
  templateUrl: './getting-started-section.component.html',
  styleUrl: './getting-started-section.component.scss'
})
export class GettingStartedSectionComponent {
  private readonly analytics = inject(AnalyticsService);

  readonly paths: GettingStartedPath[] = [
    {
      id: 'trial',
      eyebrow: 'Existing small organization',
      title: '90-Day Trial',
      description: 'Try MemberTrace with your organization before choosing a paid plan.',
      terms: [
        '90 days free',
        'Up to 50 active members',
        'Card on file at signup; not charged today',
        'Full MemberTrace product access',
        'Email, chat, and phone support',
        'Pick Society or Growth when you sign up'
      ],
      guardrail: 'The trial covers up to 50 active members and ends after 90 days.',
      ctaLabel: 'Start free trial',
      ctaLink: '/register-organization',
      analyticsId: 'pricing-trial-start-click'
    },
    {
      id: 'launch',
      eyebrow: 'Brand-new organization',
      title: 'MemberTrace Launch Program',
      description: 'Building a new organization? Start with MemberTrace while you establish your membership.',
      supportingNote: 'For qualified new organizations that are actively building their membership.',
      offerRule: '12 months or 50 active members, whichever comes first.',
      terms: [
        'Application / approval required',
        'Free while launching',
        'Simple onboarding and email support',
        'Transition to a paid plan at graduation'
      ],
      guardrail: 'Launch Program eligibility ends after 12 months or 50 active members, whichever comes first.',
      ctaLabel: 'Apply to the Launch Program',
      ctaLink: '/contact',
      queryParams: { inquiryType: 'launch-program' },
      analyticsId: 'pricing-launch-program-click'
    },
    {
      id: 'migration',
      eyebrow: 'Established organization',
      title: 'Concierge Migration',
      description: 'Skip the test environment. We\'ll help configure MemberTrace around your organization, migrate your data, and prepare your team for launch.',
      badge: 'For established organizations',
      highlighted: true,
      terms: [
        'Guided data migration',
        'Initial configuration assistance',
        'Admin onboarding',
        'Migration planning and data validation',
        'Go-live preparation',
        'Pay when you\'re ready to go live'
      ],
      ctaLabel: 'Plan your migration',
      ctaLink: '/contact',
      queryParams: { inquiryType: 'migration' },
      analyticsId: 'pricing-concierge-migration-click'
    }
  ];

  onPathCta(path: GettingStartedPath): void {
    this.analytics.trackCtaClick(path.analyticsId);
  }
}
